<template>
  <div
    class="to-do__item__wrapper"
    :class="{ 'to-do__item__wrapper_details': showDescriptionProp }"
  >
    <div
      class="to-do__item"
      :class="[
        { 'is-show-users': isSent, 'is-completed': doneToDo },
        { 'to-do__item_details': showDescriptionProp },
        `is-${item.status.toLowerCase()}`
      ]"
      :role="showDescriptionProp ? undefined : 'button'"
      :tabindex="showDescriptionProp ? undefined : 0"
      @click="onItemClick"
    >
      <button
        type="button"
        class="to-do__item__checkbox"
        :class="[{ 'pointer-none': disableCheckbox }]"
        @click.stop="onCheckboxClick"
      >
        <icon
          :data="getToDoIcon.icon"
          :color="getToDoIcon.color"
          :fill="true"
          original
          width="var(--icon-24)"
          height="var(--icon-24)"
        />
      </button>
      <div class="to-do__item__content">
        <div class="to-do__item__title-row">
          <icon
            v-if="item.urgent"
            data="@icon/flag.svg"
            class="to-do__item__flag"
            :fill="true"
            width="var(--icon-16)"
            height="var(--icon-16)"
            color="var(--serviceAlert-02)"
          />
          <BaseSubHeading
            v-if="item.sequenceNumber"
            size="medium"
            weight="500"
            color="monochrome-05"
            darkColor="monochrome-04"
            class="to-do__item__sequence-number"
          >
            TD-{{ item.sequenceNumber }}
          </BaseSubHeading>
          <div
            v-if="item.label"
            class="custom-label is-preview"
          >
            <LabelPreviewText :label="item.label" />
          </div>
          <span
            v-tooltip="{
              content: displayName,
              theme: 'info-tooltip',
              distance: 10,
              skidding: 16,
              placement: 'bottom',
              popperClass: 'to-do__tooltip-info'
            }"
            class="to-do__item__name-wrapper"
            :class="{ 'is-review': reviewItem }"
          >
            <router-link
              v-if="ticketParams?.relatedUrl"
              :to="getRouteHref(ticketParams.relatedUrl)"
              class="link to-do__item__name"
              :class="[{ 'check-done': checkedToDo }]"
              @click.stop="onRelatedUrlClick"
            >
              {{ displayName }}
            </router-link>
            <BaseSubHeading
              v-else
              v-ticket="ticketParams"
              class="to-do__item__name"
              size="medium"
              weight="400"
              color="monochrome-06"
              :class="[{ 'check-done': checkedToDo }]"
            >
              {{ displayName }}
            </BaseSubHeading>
            <BaseSubHeading
              v-if="reviewRequestTitle && createdBy.username"
              size="medium"
              weight="400"
              color="monochrome-06"
              class="to-do__item__review-requester"
              :class="{ 'check-done': checkedToDo }"
            >
              by {{ createdBy.username }}
            </BaseSubHeading>
          </span>
          <button
            v-if="showDescriptionProp && canEdit"
            id="to-do-edit-button"
            type="button"
            class="link is-large to-do__item__edit-button"
            @click.stop="onEdit"
          >
            Edit
          </button>
        </div>
        <router-link
          v-if="reviewItem && ticketParams.relatedUrl"
          :to="getRouteHref(ticketParams.relatedUrl)"
          class="to-do__item__related-item"
          :class="`is-${reviewItem.itemType?.toLowerCase()}`"
          @click.stop="onRelatedUrlClick"
        >
          <div class="to-do__item__related-item-name">
            <BaseSubHeading
              size="medium"
              weight="500"
              color="monochrome-05"
              darkColor="monochrome-04"
              class="to-do__item__related-item-number"
            >
              #{{ reviewItem.sequenceNumber }}
            </BaseSubHeading>
            <BaseSubHeading
              size="medium"
              weight="400"
              color="monochrome-07"
              class="to-do__item__related-item-title"
            >
              {{ reviewItem.title }}
            </BaseSubHeading>
          </div>
        </router-link>
        <div class="to-do__item__properties">
          <span
            v-if="deadlineStatus"
            class="to-do__item__due"
            :class="`is-${deadlineStatus}`"
          >
            {{ dueLabel }}
          </span>
          <VDropdown
            v-if="item.owners.length"
            class="to-do__item__owners"
            :disabled="!isSentInProgress && item.owners.length <= 1"
            :triggers="['hover']"
            :popperHideTriggers="['hover']"
            :popperShowTriggers="['hover']"
            :showGroup="`to-do-${index}`"
            placement="bottom-end"
            :popperClass="
              isSentInProgress ? 'to-do__item__completion-tooltip' : 'to-do__item__info__wrapper'
            "
            theme="info-dropdown"
            @click.stop
          >
            <div class="to-do__item__owner-groups">
              <div
                v-if="isSentInProgress"
                class="to-do__item__completion-progress"
              >
                <span class="to-do__item__completion-count">{{ completedOwnersCount }}</span>
                <span>of</span>
                <span class="to-do__item__completion-count">{{ item.owners.length }}</span>
                <icon
                  data="@icon/check.svg"
                  class="to-do__item__completion-check"
                  :fill="true"
                  width="var(--icon-24)"
                  height="var(--icon-24)"
                />
              </div>
              <AvatarGroupToDo
                v-else-if="isSentCompletedByEveryone"
                :avatars="item.owners"
                :numberOfVisibleUsers="5"
                type="avatarGroup"
                size="md"
              />
              <div
                v-else-if="completedOwners.length"
                class="to-do__item__completed-owners"
              >
                <AvatarGroupToDo
                  :avatars="completedOwners"
                  :numberOfVisibleUsers="5"
                  type="avatarGroup"
                  size="md"
                />
                <icon
                  data="@icon/check.svg"
                  class="to-do__item__completed-owners-check to-do__item__completion-check"
                  :fill="true"
                  width="var(--icon-16)"
                  height="var(--icon-16)"
                />
              </div>
              <AvatarGroupToDo
                v-if="!isSentInProgress && !isSentCompletedByEveryone && remainingOwners.length"
                :avatars="remainingOwners"
                :numberOfVisibleUsers="5"
                type="avatarGroup"
                size="md"
              />
            </div>
            <template #popper>
              <div class="to-do__item__owners-header">
                <BaseSubHeading
                  size="large"
                  weight="500"
                  :className="`to-do__item__owners-status is-${ownersStatus.id}`"
                >
                  {{ $t(`toDoOwnerStatus.${ownersStatus.id}`) }}
                </BaseSubHeading>
                <BaseSubHeading
                  size="medium"
                  weight="500"
                  color="monochrome-05"
                  darkColor="monochrome-04"
                >
                  {{ $t(`toDoCompletionRule.${item.completionRule}`) }}
                </BaseSubHeading>
              </div>
              <div
                v-for="(owner, ownerIndex) in item.owners"
                :key="owner.id || ownerIndex"
                class="to-do__item__info"
                :class="{ 'to-do__item__completion-tooltip-user': isSentInProgress }"
              >
                <div class="to-do__item__info-user">
                  <Avatar
                    :src="owner.pictureUrl"
                    :gap="owner.getInitials()"
                  />
                  <BaseSubHeading
                    size="large"
                    weight="400"
                    color="monochrome-06"
                  >
                    {{ owner.username }}
                  </BaseSubHeading>
                </div>
                <icon
                  v-if="item.ownersCompletionStatuses[owner.id]"
                  data="@icon/doubleCheck.svg"
                  class="to-do__item__completion-check"
                  :fill="true"
                  width="var(--icon-24)"
                  height="var(--icon-24)"
                />
              </div>
            </template>
          </VDropdown>
        </div>
      </div>
    </div>
    <div
      v-if="showDescriptionProp"
      class="to-do__item__body to-do__item__body_details"
      :class="{ 'is-review': reviewRequestTitle }"
    >
      <div
        v-if="createdBy.id && !reviewRequestTitle"
        class="to-do__item__created-by"
      >
        <Avatar
          :src="createdBy.pictureUrl ?? null"
          :gap="createdBy.getInitials()"
          bordered
        />
        <BaseSubHeading
          size="large"
          weight="500"
          color="monochrome-06"
          class="to-do__item__created-by__name"
        >
          {{ createdBy.username }}
        </BaseSubHeading>
        <BaseSubHeading
          size="medium"
          weight="400"
          color="monochrome-06"
        >
          assigned you a new ToDo Item:
        </BaseSubHeading>
      </div>
      <router-link
        v-if="item.item && ticketParams.relatedUrl && !reviewRequestTitle"
        :to="getRouteHref(ticketParams.relatedUrl)"
        class="to-do__item__review-resource"
        @click.stop="onRelatedUrlClick"
      >
        <BaseSubHeading
          size="large"
          weight="500"
          color="monochrome-05"
          darkColor="monochrome-04"
          class="to-do__item__review-resource-number"
        >
          #{{ item.item.sequenceNumber }}
        </BaseSubHeading>
        <div
          v-if="item.item.label"
          class="custom-label is-preview"
        >
          <LabelPreviewText :label="item.item.label" />
        </div>
        <BaseSubHeading
          size="large"
          weight="400"
          color="monochrome-07"
          darkColor="monochrome-06"
          class="to-do__item__review-resource-title"
        >
          {{ item.item.title }}
        </BaseSubHeading>
      </router-link>
      <div
        v-if="reviewRequestTitle"
        class="to-do__item__message"
      >
        <div
          v-if="createdBy.id"
          class="to-do__item__message-author"
        >
          <Avatar
            :src="createdBy.pictureUrl ?? null"
            :gap="createdBy.getInitials()"
            bordered
          />
          <BaseSubHeading
            size="medium"
            weight="600"
            color="monochrome-06"
          >
            {{ createdBy.username }}:
          </BaseSubHeading>
        </div>
        <Editor
          id="body-description"
          type="read"
          class="to-do__item__description to-do__item__description_review"
          :targetConfig="typeConfig"
          :value="item.structuredDescription.html"
          :extensions="extensions"
          :isBodyColor="true"
        />
      </div>
      <Editor
        v-else
        id="body-description"
        type="read"
        class="to-do__item__description"
        :targetConfig="typeConfig"
        :value="item.structuredDescription.html"
        :extensions="extensions"
        :isBodyColor="true"
      />
      <div
        v-if="reviewRequestTitle && attachments.length"
        class="to-do__item__files"
      >
        <div
          v-for="file in attachments"
          :key="file.id"
          class="to-do__item__file"
        >
          <FilePic :fileName="file.name" />
          <div class="to-do__item__file-info">
            <BaseSubHeading
              weight="500"
              size="small"
              color="monochrome-07"
              className="to-do__item__file-name"
            >
              {{ file.name }}
            </BaseSubHeading>
            <BaseSubHeading
              weight="500"
              size="small"
              color="monochrome-05"
              darkColor="monochrome-04"
            >
              {{ getFileSize(file.size) }}
            </BaseSubHeading>
          </div>
        </div>
      </div>
      <div class="to-do__item__footer">
        <BaseSubHeading
          size="extra-small"
          weight="400"
          color="monochrome-05"
          darkColor="monochrome-04"
          className="to-do__item__created-time"
        >
          {{ createdTimeAgo }}
        </BaseSubHeading>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Emit, Prop } from "vue-property-decorator"
import { formatRelative, isBefore, isSameDay } from "date-fns"
import enUS from "date-fns/locale/en-US"
import { Action, Getter } from "s-vuex-class"

import AvatarGroupToDo from "@/components/Avatar/AvatarGroupToDo.vue"
import Avatar from "@/components/Avatar/index.vue"
import Editor from "@/components/Editor/index.vue"
import FilePic from "@/components/File/FilePic.vue"
import LabelPreviewText from "@/components/Label/LabelPreviewText.vue"

import { ticket } from "@/directives"
import formatBytes from "@/helpers/formatBytes"
import { FileMetaData, UploadAttachment, User } from "@/models"
import { ToDo } from "@/models/ToDo"
import { Attachment } from "@/plugins/extensions/Attachment"
import { ReferenceAllUser, ReferenceAt } from "@/plugins/extensions/reference/referenceAt"
import {
  ReferenceHashEpic,
  ReferenceHashFile,
  ReferenceHashIdea,
  ReferenceHashItem,
  ReferenceHashMilestone,
  ReferenceHashProject,
  ReferenceHashSprint,
  ReferenceHashTask,
  ReferenceHashTeam,
  ReferenceHashTip
} from "@/plugins/extensions/reference/referenceHash"
import { ReferenceLink } from "@/plugins/extensions/reference/referenceLink"
import { Actions, Getters } from "@/store"
import { formatDate, formatDateAgo, getTodayCalendarDate, toCalendarDate } from "@/utils/dateUtil"

interface TicketParams {
  wholeLink: boolean
  query: any
  todo: ToDo
  params: any
  routerName: string
  relatedUrl: string
}

@Options({
  name: "ToDoItem",
  components: {
    LabelPreviewText,
    ToDo,
    AvatarGroupToDo,
    Editor,
    Avatar,
    FilePic
  },
  directives: {
    ticket
  }
})
export default class ToDoItem extends Vue {
  @Prop({ required: true }) readonly item: ToDo
  @Prop({ default: 0 }) readonly index: number
  @Prop({ default: false }) readonly sm: boolean
  @Prop({ default: false }) readonly isSent: boolean
  @Prop({ default: false }) readonly disableCheckbox: boolean
  @Prop({ default: false }) readonly showDescriptionProp: boolean
  @Action(Actions.ATTACHMENT_UPLOAD) attachmentUpload: (
    payload: UploadAttachment
  ) => Promise<FileMetaData[]>

  @Getter(Getters.GET_USER_CURRENT_PROFILE) readonly currentUser: User
  @Getter(Getters.THEME_IS_DARK) readonly isDark: boolean

  left: any
  completionRequested: boolean | null = null

  get doneToDo() {
    return this.item.status === "COMPLETED"
  }

  get checkedToDo() {
    return this.completionRequested ?? (this.item.completedByMe || this.doneToDo)
  }

  get typeConfig() {
    return null
  }

  get attachments(): FileMetaData[] {
    return this.item.fileMetadata?.filter(file => !file.deleted) ?? []
  }

  getFileSize(size: number) {
    return formatBytes(size.toString())
  }

  get createdTimeAgo() {
    return formatDateAgo({
      date: new Date(),
      dateFormat: this.item.createdDate,
      short: true,
      sufixAgo: true
    })
  }

  /** Renders file references inserted into ToDo descriptions through the editor or AI Widget. */
  get extensions() {
    return [
      Attachment(this.attachmentUpload as any, this.typeConfig, true),
      ReferenceAt,
      ReferenceAllUser,
      ReferenceHashProject,
      ReferenceHashMilestone,
      ReferenceHashEpic,
      ReferenceHashIdea,
      ReferenceHashFile,
      ReferenceHashTeam,
      ReferenceHashSprint,
      ReferenceHashItem,
      ReferenceHashTask,
      ReferenceHashTip,
      ReferenceLink
    ]
  }

  get createdBy() {
    return this.item.createdBy
  }

  /** Allows the creator to edit only manually created ToDos. */
  get canEdit() {
    return !this.item.createdAutomatically && this.item.createdBy.id === this.currentUser.id
  }

  /** Separates owners who resolved a completed ToDo so they can be identified at a glance. */
  get completedOwners() {
    if (!this.doneToDo) return []
    return this.item.owners.filter(owner => this.item.ownersCompletionStatuses[owner.id])
  }

  get completedOwnersCount() {
    return this.item.owners.filter(owner => this.item.ownersCompletionStatuses[owner.id]).length
  }

  /** Maps owner completion progress to the presentation used by the assignee list. */
  get ownersStatus() {
    if (this.item.status === "COMPLETED") {
      return { id: "completed" }
    }
    if (this.completedOwnersCount) {
      return { id: "in-progress" }
    }
    return { id: "assigned-to" }
  }

  get isSentInProgress() {
    return this.isSent && this.item.status !== "COMPLETED"
  }

  get isSentCompletedByEveryone() {
    return (
      this.isSent && this.item.status === "COMPLETED" && this.item.completionRule === "EVERYONE"
    )
  }

  get remainingOwners() {
    if (!this.completedOwners.length) return this.item.owners
    return this.item.owners.filter(owner => !this.item.ownersCompletionStatuses[owner.id])
  }

  /** Uses the automatic review type as the display label instead of parsing the generated name. */
  get reviewRequestTitle() {
    if (this.item.type === "PM_REVIEW_REQUEST") return "PM Review Request"
    if (this.item.type === "CLIENT_REVIEW_REQUEST") return "Client Review Request"
    if (this.item.type === "TASK_BREAKDOWN") return "Task Breakdown Request"
    return ""
  }

  /** Linked Item shown with its full title so the request context is clear without opening it. */
  get reviewItem() {
    return this.reviewRequestTitle ? this.item.item : undefined
  }

  get displayName() {
    return this.reviewItem ? this.reviewRequestTitle : this.item.name
  }

  get getToDoIcon() {
    return {
      icon: require(`@icon/${this.checkedToDo ? "checkbox-checked" : "checkbox-unchecked"}.svg`),
      color: `var(--${this.isDark ? "accent-200" : "accent-03"})`
    }
  }

  get deadlineDate() {
    return this.item.estimatedDeadline ? toCalendarDate(this.item.estimatedDeadline) : null
  }

  get deadlineStatus() {
    if (this.doneToDo) return "completed"
    if (this.item.urgent) return "urgent"
    if (!this.deadlineDate) return undefined
    if (isSameDay(this.deadlineDate, getTodayCalendarDate())) return "today"
    if (isBefore(this.deadlineDate, getTodayCalendarDate())) return "past-due"
    return "scheduled"
  }

  get dueLabel() {
    if (this.doneToDo) return "Completed"
    if (this.item.urgent) return "Urgent"
    if (!this.deadlineDate) return undefined
    if (isSameDay(this.deadlineDate, getTodayCalendarDate())) return "Today"
    if (this.timeAgo === "Tomorrow") return "Tomorrow"
    return formatDate(this.deadlineDate, "d MMM, yyyy", { dateOnly: true })
  }

  get ticketParams() {
    let result: TicketParams = {
      wholeLink: true,
      query: {},
      todo: this.item,
      params: {},
      routerName: "",
      relatedUrl: ""
    }
    if (this.item?.relatedUrl) {
      result = { ...result, relatedUrl: this.item.relatedUrl }
    }
    return result
  }

  get timeAgo() {
    const formatRelativeLocale = {
      lastWeek: "'Last Week'",
      yesterday: "'Yesterday'",
      today: "'Today'",
      tomorrow: "'Tomorrow'",
      nextWeek: "'Next Week'",
      other: "dd.MM.yyyy"
    }
    const locale = {
      ...enUS,
      formatRelative: token => formatRelativeLocale[token]
    }
    if (!this.deadlineDate) return this.left
    this.left = formatRelative(this.deadlineDate, getTodayCalendarDate(), {
      locale,
      weekStartsOn: 1
    })
    return this.left
  }

  @Emit()
  onCheckboxClick() {
    this.completionRequested = !this.checkedToDo
    const status = this.completionRequested ? "COMPLETED" : "ACTIVE"

    return {
      status,
      toDoItemId: this.item.id
    }
  }

  @Emit()
  onRelatedUrlClick() {
    return
  }

  getRouteHref(param: string) {
    const { pathname, search } = new URL(param)
    const routeData = this.$router.resolve(`${pathname}${search}`)
    return routeData.href
  }

  /** Opens list items directly in the details view and ignores clicks inside details. */
  @Emit("onOpen")
  onOpen() {
    if (this.showDescriptionProp) return
    return this.item
  }

  /** Keeps nested links and controls interactive while making the rest of the list item clickable. */
  onItemClick(event: MouseEvent) {
    const target = event.target as HTMLElement

    if (target.closest("a, button")) return
    this.onOpen()
  }

  @Emit("onEdit")
  onEdit() {
    return this.item
  }
}
</script>
